import { ReactElement, useState } from 'react';
import { useUIDSeed } from 'react-uid';

import { Route, ApiRoute } from 'config';
import { addDays, weekAndMonthDay, monthDayAndYear } from 'utils/dateTimes';
import { post } from 'utils/network';

import WelcomeSurvey from 'models/WelcomeSurvey';
import useCurrentUser from 'hooks/useCurrentUser';

import Modal from 'components/shared/Modal';
import RadioButton from 'components/shared/RadioButton';
import RangeSlider from 'components/shared/RangeSlider';
import { Component as RightArrowIcon } from 'icons/icon-ui--arrow-right-outline.svg';

type Props = { onClose: () => void };

function WelcomeSurveyModal({ onClose }: Props): ReactElement {
  const seed = useUIDSeed();
  const { currentUser } = useCurrentUser();

  const {
    next_scheduled_order_date: nextScheduledOrderDate,
  } = currentUser.subscription || {};

  const [currentStep, setCurrentStep] = useState(0);
  const currentStepKey = WelcomeSurvey.questions[currentStep].key;

  const [surveyResult, setSurveyResult] = useState(
    WelcomeSurvey.getEmptySurvey(),
  );

  const isCurrentStepCompleted = surveyResult[currentStepKey] === 'Other'
    ? surveyResult[`${currentStepKey}_other`]
    : surveyResult[currentStepKey];

  const isLastStep = currentStep === WelcomeSurvey.questions.length - 1;

  const displayInARow = [
    'how_many_meals_each_week',
    'using_pc',
    'have_you_tried_other_meals',
    'gender',
    'number_of_people_in_household',
  ].includes(currentStepKey);

  const displayCentered = [
    'eating_habits',
    'how_often_go_to_the_gym',
  ].includes(currentStepKey);

  const displaySingleInput = currentStepKey === 'birthday';

  if (window.location.hash === '#welcome') {
    window.history.replaceState(null, '', Route.UPCOMING_MENUS);
  }

  const handleAnswerSelection = (answer) => {
    setSurveyResult({
      ...surveyResult,
      [currentStepKey]: answer,
    });
  };

  const handleOtherAnswerSelection = (answer) => {
    setSurveyResult({
      ...surveyResult,
      [`${currentStepKey}_other`]: answer,
    });
  };

  const handleNextClick = () => {
    setCurrentStep((step) => (
      step < WelcomeSurvey.questions.length ? step + 1 : step
    ));
  };

  const handleRemindMeLater = () => {
    localStorage.setItem(
      'showWelcomeSurvey',
      addDays(new Date(), 1).toUTCString(),
    );
    onClose();
  };

  const handleClose = () => {
    localStorage.removeItem('showWelcomeSurvey');
    onClose();
  };

  const handleSave = async () => {
    await post(ApiRoute.WELCOME_SURVEY, { welcome_survey: surveyResult });
    handleClose();
  };

  return (
    <Modal
      show
      onClose={handleClose}
      className="c-modal-welcome-survey"
      header={(
        <div>
          <Modal.Header>
            Welcome!
          </Modal.Header>
          <p className="u-text--center">
            We’re so glad you’re here. And we can’t wait for you try
            these meals, they make it tasty and so easy to eat well.
            Your subscription is confirmed.&nbsp;
            <strong>
              Your first delivery should arrive on&nbsp;
              {weekAndMonthDay(nextScheduledOrderDate, false)}
              .
            </strong>
          </p>
        </div>
      )}
      body={(
        <>
          <Modal.Header>
            Tell us more
          </Modal.Header>
          <p className="u-text--center">
            We’re passionate about making Purple Carrot an experience
            you’ll love. Please take this quick survey.
          </p>
          <fieldset className="c-form-group">
            <legend className="u-text--center">
              {WelcomeSurvey.questions[currentStep].label}
            </legend>
            <div
              className={[
                'c-modal-welcome-survey__answers',
                displayInARow
                  ? 'c-modal-welcome-survey__answers--row'
                  : '',
                displayCentered
                  ? 'c-modal-welcome-survey__answers--center'
                  : '',
                displaySingleInput
                  ? 'c-modal-welcome-survey__answers--single-input'
                  : '',
              ].join(' ')}
            >
              {![
                'food_is_medicine',
                'fun_to_make_the_meals',
                'birthday',
              ].includes(currentStepKey) && (
                <>
                    {WelcomeSurvey.questions[currentStep]?.answers?.map(
                      (answer, i) => (
                        <RadioButton
                          key={seed(answer)}
                          id={`${currentStepKey}_${i}`}
                          label={answer}
                          name="answer"
                          onChange={() => handleAnswerSelection(answer)}
                        />
                      ),
                    )}
                  {surveyResult[currentStepKey] === 'Other' && (
                    <input
                      id="other_reason"
                      placeholder="Please specify"
                      type="text"
                      onChange={(e) => handleOtherAnswerSelection(
                        e.target.value,
                      )}
                    />
                  )}
                </>
              )}

              {[
                'food_is_medicine',
                'fun_to_make_the_meals',
              ].includes(currentStepKey) && (
                <RangeSlider
                  id={currentStepKey}
                  leftLabel="Strongly disagree"
                  rightLabel="Strongly agree"
                  name={currentStepKey}
                    // @ts-expect-error TODO: fix null safety
                  steps={WelcomeSurvey.questions[currentStep].answers}
                  onChange={(value) => handleAnswerSelection(value)}
                />
              )}

              {currentStepKey === 'birthday' && (
                <input
                  id="birthday"
                  placeholder="mm/dd/yyyy"
                  type="date"
                  onChange={(e) => handleAnswerSelection(
                    monthDayAndYear(new Date(e.target.value)),
                  )}
                />
              )}
            </div>
          </fieldset>
        </>
      )}
      footer={(
        <>
          <button
            type="button"
            className="c-link"
            onClick={handleRemindMeLater}
          >
            Remind me later
          </button>

          <button
            type="button"
            className="c-button c-button--primary"
            onClick={isLastStep ? handleSave : handleNextClick}
            disabled={!isCurrentStepCompleted}
          >
            <span>
              {currentStep + 1}
              /
              {WelcomeSurvey.questions.length}
            </span>
            {isLastStep ? ' - Save' : (
              <>
                &nbsp;-&nbsp;Next&nbsp;&nbsp;
                <RightArrowIcon className="c-svg-fill-white-color" />
              </>
            )}
          </button>
        </>
      )}
    />
  );
}

export default WelcomeSurveyModal;
